import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormulaFieldEntity } from '../../../data-models/formula-field.data-model';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { formulaFieldFormStepState } from './FormulaFieldFormStateJ';

/**
 * Where each entity's formula column renders — the display step and the bulk
 * modal name the target the switch controls (formulaFieldEntityForView /
 * the security colDef injection decide the actual grids).
 */
export const FORMULA_DISPLAY_TARGET_LABEL: Record<FormulaFieldEntity, string> = {
  company: 'Company Views',
  round: 'Round Tracker',
  captableInvestment: 'Security Overview',
};

interface FormulaFieldDisplayStepProps {
  isEdit: boolean;
  /** Controlled by the modal's gate so Previous/Next never re-seeds it. */
  showInGrid: boolean;
  onShowInGridChange: (showInGrid: boolean) => void;
  /** Saves the field with the current flag; resolves false to stay on the step. */
  onSave: () => Promise<boolean>;
}

/**
 * "Set Field Display" step: unlike a KPI (period × section combos in
 * fieldDisplayConfig), a formula maps to one column on its entity's view(s),
 * so display config is a single switch stored on the field itself.
 */
export function FormulaFieldDisplayStep({
  isEdit,
  showInGrid,
  onShowInGridChange,
  onSave,
}: FormulaFieldDisplayStepProps) {
  const methods = useFormContext();
  const entity = (methods.getValues('entity') as FormulaFieldEntity) ?? 'company';
  const targetLabel = FORMULA_DISPLAY_TARGET_LABEL[entity] ?? FORMULA_DISPLAY_TARGET_LABEL.company;
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      return await onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '30rem', py: '1rem' }}>
        <Typography variant='subtitle1'>Set Field Display</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showInGrid}
              disabled={saving}
              onChange={(e) => onShowInGridChange(e.target.checked)}
            />
          }
          label={`Include in ${targetLabel}`}
        />
        {!showInGrid && (
          <Typography variant='caption' color='text.secondary'>
            The column will not appear on {targetLabel} or in its column picker. The field still evaluates, so
            other formulas can keep referencing it.
          </Typography>
        )}
      </Box>
      <StepperFormButtons
        stepState={formulaFieldFormStepState}
        stepIsValid={save}
        nextButtonLabel={isEdit ? 'Save' : 'Create'}
        incrementOnNext={false}
      />
    </>
  );
}
